import React, { useEffect, useState } from "react";
import {
  convertLocalDate,
  convertProfileLocalDate,
} from "../../Utils/configController";
import * as url from "../../helpers/url_helper";
import { useParams } from "react-router-dom";
import ReactDataTable from "../../components/Common/ReactDataTable";
import {
  Collapse,
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
  Button,
  CardHeader,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { Dayjs } from "dayjs";
import { useSocket } from "../../Utils/socket";
import { OPERATE_MQTT } from "../../helpers/socketEvent";
import { FormatJsonCheckBox } from "../../components/Common/button";
import Request from "../../helpers/request";
import OtaModal from "./Users/ProfileList/OtaModal";

// Commands shown in the filter dropdown, according to device type.
// Edit these lists to match the commands your devices actually send.
const CMD_OPTIONS: Record<string, string[]> = {
  chip: ['OL', 'DL', 'SN', 'SE', 'DS','OT', 'ST','HW', 'TA','PG','SS', 'SM','SQ', 'UT','TL'],
  aircurtain: ['OL', 'DL', 'SN', 'SE', 'DS','OT', 'ST','HW', 'TA','PG','SS', 'SM','SQ', 'UT','TL'],
  blaster: [ 'BOTA', 'BIRT', 'BIRS', 'BIRL',  'BSTS',  'BONL', 'BTMP', 'BRBT', 'BFCT', 'BSCH', 'BSCN', 'BNET', 'BCHN','BRMT'],
  airgateway: ['UT','UU','PG', 'SS','DS', 'OT', 'ST','OL','DL', 'SN', 'CN','AN', 'RN', 'GN', 'GG', 'GC', 'GS',  'GT',  'RT', 'NH', 'HA'],
};

const emptyFilters: {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  cmd: string[];
} = {
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  cmd: [],
};

// Dayjs -> "2026-10-30" (same format the native date input used to send)
const formatDate = (date: Dayjs | null) =>
  date && date.isValid() ? date.format("YYYY-MM-DD") : "";

// Dayjs -> "10:30 PM" (the API expects this format)
const formatTime12 = (time: Dayjs | null) =>
  time && time.isValid() ? time.format("hh:mm A") : "";

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (e) {
    return false;
  }
};

const DebugDetail = () => {
  const socket: any = useSocket();
  const { slave } = useParams();
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");

  const [isReload, setReload] = useState(false);
  const [showFormatted, setShowFormatted] = useState<boolean>(false);
  const [txCount, setTxCount] = useState<number>(0);
  const [rxCount, setRxCount] = useState<number>(0);
  const [cmdCounts, setCmdCounts] = useState({});

  const [loading, setLoading] = useState<boolean>(false);
  const [profileDetails, setProfileDetails] = useState<any[]>([]);
  const [deviceDetails, setDeviceDetails] = useState<any>({});

  const [isOpen, setIsOpen] = useState(false);
  const [isOtaOpen, setIsOtaOpen] = useState(false);
  const [isSSModalOpen, setIsSSModalOpen] = useState(false);
  const [ssData, setSsData] = useState<any>(null);
  const [nodeSearch, setNodeSearch] = useState("");
  const [filterOnline, setFilterOnline] = useState(false);
  const [filterOffline, setFilterOffline] = useState(false);
  const [filterPowerOn, setFilterPowerOn] = useState(false);
  const [filterPowerOff, setFilterPowerOff] = useState(false);

  // Debug list filters
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>(
    {},
  );
  const [isCmdOpen, setIsCmdOpen] = useState(false);
  const [cmdSearch, setCmdSearch] = useState("");

  const cmdOptions =
    CMD_OPTIONS[deviceDetails?.deviceType?.toLowerCase() || ""] || [];

  // commands matching the search text in the dropdown
  const filteredCmdOptions = cmdOptions.filter((c) =>
    c.toLowerCase().includes(cmdSearch.trim().toLowerCase()),
  );

  const handleSSClick = (command: string) => {
    try {
      const parsed = JSON.parse(command);
      setSsData(parsed);
      setIsSSModalOpen(true);
    } catch (e) {
      console.log("Failed to parse SS command", e);
    }
  };
  const toggle = () => setIsOpen(!isOpen);

  // click on a TX cell -> copy its JSON
  const handleCopyJson = async (text: string) => {
    // let the user select part of the text without triggering a copy
    if (window.getSelection()?.toString()) return;
    const ok = await copyToClipboard(text);
    if (ok) toast.success("JSON copied", { toastId: "copy-json" });
    else toast.error("Copy failed", { toastId: "copy-json-error" });
  };

  // used by the MUI date / time pickers
  const handleDateTimeChange = (
    name: "startDate" | "endDate" | "startTime" | "endTime",
    value: Dayjs | null,
  ) => setFilters((prev) => ({ ...prev, [name]: value }));

  // Multi-select command helpers
  const toggleCmd = (c: string) =>
    setFilters((prev) => ({
      ...prev,
      cmd: prev.cmd.includes(c)
        ? prev.cmd.filter((x) => x !== c)
        : [...prev.cmd, c],
    }));

  // selects everything currently visible, keeping earlier selections
  const selectAllCmds = () =>
    setFilters((prev) => ({
      ...prev,
      cmd: Array.from(new Set([...prev.cmd, ...filteredCmdOptions])),
    }));

  const clearCmds = () => setFilters((prev) => ({ ...prev, cmd: [] }));

  const toggleCmdDropdown = () => {
    setIsCmdOpen((open) => {
      if (open) setCmdSearch(""); // clear the search when the menu closes
      return !open;
    });
  };

  const handleApplyFilters = () => {
    if (
      filters.startDate &&
      filters.endDate &&
      filters.startDate.isAfter(filters.endDate, "day")
    ) {
      toast.error("Start date cannot be after end date");
      return;
    }

    const startDate = formatDate(filters.startDate);
    const endDate = formatDate(filters.endDate);
    const startTime = formatTime12(filters.startTime);
    const endTime = formatTime12(filters.endTime);

    // only send the fields that have a value
    const params: Record<string, string> = {};
    const hasDate = startDate || endDate;
    if (hasDate) {
      // if only one date is chosen, use it for both
      params.startDate = startDate || endDate;
      params.endDate = endDate || startDate;
      // a date range needs a time range, so default to the whole day
      params.startTime = startTime || "12:00 AM";
      params.endTime = endTime || "11:59 PM";
    } else {
      if (startTime) params.startTime = startTime;
      if (endTime) params.endTime = endTime;
    }

    // multiple commands are sent comma-separated, e.g. "OL,DL,SS"
    if (filters.cmd.length) params.cmd = filters.cmd.join(",");
    setAppliedFilters(params);
  };

  const handleResetFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters({});
    setCmdSearch("");
  };

  const getUsersDetail = () => {
    setLoading(true);
    const reqObj = {
      slave: slave,
    };
    Request.post(url.FINDUSERBYSLAVE, reqObj)
      .then((res) => {
        const data = res?.data?.userDetail;
        setDeviceDetails(res?.data?.deviceInfo);
        setProfileDetails([
          {
            name: "User Id",
            data: `#${data?.id}`,
          },
          {
            name: "Name",
            data: `${data?.firstname} ${data?.lastname}`,
          },
          {
            name: "Email",
            data: data?.email ? data?.email : "--",
          },
          {
            name: "Mobile",
            data: data?.mobile ? data?.mobile : "--",
          },
          {
            name: "Address",
            data: data?.address ? data?.address : "--",
          },
          {
            name: "Created At",
            data: data?.createdAt
              ? convertProfileLocalDate(data?.createdAt)
              : "--",
          },
          {
            name: "Last login",
            data: data?.lastLogin
              ? convertProfileLocalDate(data?.lastLogin)
              : "--",
          },
          {
            name: "Last Action",
            data: data?.lastAction
              ? convertProfileLocalDate(data?.lastAction)
              : "--",
          },
          {
            name: "Last Online Time",
            data: data?.lastOnlineTime
              ? convertProfileLocalDate(data?.lastOnlineTime)
              : "--",
          },
          {
            name: "Last Offline Time",
            data: data?.lastOfflineTime
              ? convertProfileLocalDate(data?.lastOfflineTime)
              : "--",
          },
        ]);

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getUsersDetail();
  }, [slave]);

  const handleRefresh = () => {
    setReload((prev) => !prev);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const columns = [
    {
      name: "Command",
      cell: (row: any) => {
        try {
          const parsed = JSON.parse(row?.command);
          const cmd = parsed.cmd ? parsed.cmd : "-";

          const isTX = row.topic.includes("/TX") || row.topic.includes("/g2m");
          const isRX = row.topic.includes("/RX") || row.topic.includes("/m2g");

          const textColor = isTX ? "green" : isRX ? "orange" : "inherit";
          const isAirGateway =
            deviceDetails?.deviceType?.toLowerCase() === "airgateway";
          const isClickableSS = isAirGateway && cmd === "SS" && isTX;

          if (isClickableSS) {
            return (
              <div
                style={{
                  color: textColor,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
                onClick={() => handleSSClick(row?.command)}
              >
                {cmd}
              </div>
            );
          }

          return <div style={{ color: textColor }}>{cmd}</div>;
        } catch (e) {
          return "-";
        }
      },
      selector: (row: any) => {
        try {
          const parsed = JSON.parse(row?.command);
          return parsed.cmd || "";
        } catch (e) {
          return "";
        }
      },
      sortable: true,
      wrap: true,
      grow: 0.5,
    },
    {
      name: `TX (${txCount})`,
      cell: (row: any) => {
        const isDevice =
          row.topic.includes("/TX") || row.topic.includes("/g2m");
        const content = isDevice
          ? showFormatted
            ? formatCommand(row.command)
            : row.command
          : "-";
        return (
          <div
            title={isDevice ? "Click to copy JSON" : undefined}
            onClick={isDevice ? () => handleCopyJson(content) : undefined}
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              padding: "10px 2px",
              lineHeight: "1.4",
              fontSize: "14px",
              cursor: isDevice ? "pointer" : "default",
            }}
          >
            {content}
          </div>
        );
      },
      wrap: true,
      grow: 2,
    },
    {
      name: `RX (${rxCount})`,
      cell: (row: any) => {
        const isMobile =
          row.topic.includes("/RX") || row.topic.includes("/m2g");
        const content = isMobile
          ? showFormatted
            ? formatCommand(row.command)
            : row.command
          : "-";
        return (
          <div
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              padding: "10px 2px",
               cursor: isMobile ? "pointer" : "default",
            }}
             onClick={isMobile ? () => handleCopyJson(content) : undefined}
          >
            {content}
          </div>
        );
      },
      wrap: true,
      grow: 2,
    },
    {
      name: "Created At",
      selector: (row: any) => row.createdAt, // raw date value
      sortable: true,
      format: (row: any) => row.createdAtIST, // formatted display
      wrap: true,
    },
  ];

  const formatCommand = (command: string) => {
    try {
      return JSON.stringify(JSON.parse(command), null, 2);
    } catch {
      return command;
    }
  };

  const handlePG = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "PG",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`Ping sent to slave ${slave}`);
    }
  };

  const handleDS = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "DS",
      };

      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`DS sent to slave ${slave}`);
    }
  };
  const handleDL = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "DL",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`DL sent to slave ${slave}`);
    }
  };
  const handleSS = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "SS",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`SS sent to slave ${slave}`);
    }
  };
  const handleGN = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "GN",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`GN sent to slave ${slave}`);
    }
  };
  const handleGG = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "GG",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`GG sent to slave ${slave}`);
    }
  };
  const handleGC = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "GC",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`GC sent to slave ${slave}`);
    }
  };
  const handleReboot = () => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "HW",
        sub_cmd: "reboot",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`Reboot sent to slave ${slave}`);
    }
  };

  const handleBSCH = () => {
    if (slave !== undefined && deviceDetails?.token) {
      const reqObj = {
        device_id: slave,
        cmd: "BSCH",
        sub_cmd: "get_info",
        tag: `airpanel-${userInfo?.id}`,
        token: deviceDetails?.token,
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`BSCH sent to slave ${slave}`);
    }
  };
  const handleBSCN = () => {
    if (slave !== undefined && deviceDetails?.token) {
      const reqObj = {
        device_id: slave,
        cmd: "BSCN",
        sub_cmd: "get_info",
        tag: `airpanel-${userInfo?.id}`,
        token: deviceDetails?.token,
      };

      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`BSCN sent to slave ${slave}`);
    }
  };
  const handleBRBT = () => {
    if (slave !== undefined && deviceDetails?.token) {
      const reqObj = {
        device_id: slave,
        cmd: "BRBT",
        tag: `airpanel-${userInfo?.id}`,
        token: deviceDetails?.token,
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`BRBT sent to slave ${slave}`);
    }
  };

  const openOtaModal = () => {
    if (!deviceDetails?.slave && !slave) {
      toast.error("Slave not found");
      return;
    }
    setIsOtaOpen(true);
  };

  const filteredRows = (ssData?.data?.node_id || [])
    .map((nodeId: any, idx: number) => ({ nodeId, idx }))
    .filter(({ nodeId }: any) =>
      String(nodeId).toLowerCase().includes(nodeSearch.toLowerCase()),
    )
    .filter(({ idx }: any) => {
      const status = String(ssData.data.status?.[idx] ?? "");
      const isPowerOn = status.startsWith("1");
      const isOnline = status.endsWith("1");

      if (filterOnline && !isOnline) return false;
      if (filterOffline && isOnline) return false;
      if (filterPowerOn && !isPowerOn) return false;
      if (filterPowerOff && isPowerOn) return false;

      return true;
    });

  return (
    <>
      <ToastContainer />
      {isOtaOpen && (
        <OtaModal
          headerTitle="OTA Update"
          otaSlave={{
            ...deviceDetails,
            slave: deviceDetails?.slave || slave,
            product:
              deviceDetails?.deviceType === "blaster"
                ? "airblaster"
                : deviceDetails?.product?.toLowerCase(),
          }}
          cancleBtnClick={() => setIsOtaOpen(false)}
          isHideLog={true}
        />
      )}
      <Card
        style={{
          marginTop: 100,
          marginLeft: 30,
          marginRight: 30,
          marginBottom: 0,
        }}
      >
        <CardHeader
          className="d-flex justify-content-between align-items-center px-4 py-3"
          style={{
            backgroundColor: "#fff",
            borderBottom: isOpen ? "1px solid #e9ecef" : "none",
          }}
        >
          {/* LEFT */}
          <div className="">
            {(() => {
              const DEVICE_ACTIONS: Record<string, any[]> = {
                chip: [
                  { label: "PG", onClick: handlePG },
                  { label: "DS", onClick: handleDS },
                  { label: "DL", onClick: handleDL },
                  { label: "SS", onClick: handleSS },
                  { label: "OTA", onClick: openOtaModal, color: "primary" },
                  {
                    label: "Reboot",
                    onClick: handleReboot,
                    color: "danger",
                  },
                ],
                blaster: [
                  { label: "BSCH", onClick: handleBSCH },
                  { label: "BSCN", onClick: handleBSCN },
                  { label: "BRBT", onClick: handleBRBT },
                  { label: "OTA", onClick: openOtaModal, color: "primary" },
                ],
                aircurtain: [
                  { label: "PG", onClick: handlePG },
                  { label: "DS", onClick: handleDS },
                  { label: "DL", onClick: handleDL },
                  { label: "SS", onClick: handleSS },
                  { label: "OTA", onClick: openOtaModal, color: "primary" },
                  {
                    label: "Reboot",
                    onClick: handleReboot,
                    color: "danger",
                  },
                ],
                iotdevice: [
                  { label: "PG", onClick: handlePG },
                  { label: "DS", onClick: handleDS },
                  { label: "DL", onClick: handleDL },
                  { label: "SS", onClick: handleSS },
                  { label: "OTA", onClick: openOtaModal, color: "primary" },
                  {
                    label: "Reboot",
                    onClick: handleReboot,
                    color: "danger",
                  },
                ],
                airgateway: [
                  { label: "PG", onClick: handlePG },
                  { label: "DL", onClick: handleDL },
                  { label: "SS", onClick: handleSS },
                  { label: "GN", onClick: handleGN },
                  { label: "GG", onClick: handleGG },
                  { label: "GC", onClick: handleGC },
                  { label: "OTA", onClick: openOtaModal, color: "primary"}
                ],
              };
              const deviceType = deviceDetails?.deviceType?.toLowerCase();

              const actions = DEVICE_ACTIONS[deviceType || ""] || [];

              return (
                <div className="d-flex flex-wrap gap-3">
                  {actions.map((btn, index) => (
                    <button
                      key={index}
                      className={`btn btn-${btn.color || "dark"}`}
                      onClick={btn.onClick}
                      // disabled={!deviceDetails?.isOnline}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* RIGHT BUTTON */}
          <Button
            color="btn btn-dark"
            size="sm"
            onClick={toggle}
            className="  d-flex align-items-center gap-2"
          >
            <i
              className={`bx ${isOpen ? "bx-chevron-up" : "bx-chevron-down"}`}
              style={{ fontSize: "24px" }}
            ></i>
          </Button>
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <CardBody>
            <Row>
              {profileDetails?.length > 0 && !loading ? (
                <>
                  <Col xs={12} md={3}>
                    {profileDetails
                      .slice(0, 5)
                      .map((item: any, index: number) => (
                        <p
                          key={index}
                          style={{ color: "#858D9D", fontSize: 14 }}
                          className="my-2"
                        >
                          {item?.name}:{" "}
                          {item?.name === "User Id" ? (
                            (() => {
                              const userId = item?.data?.startsWith("#")
                                ? item.data.slice(1)
                                : item?.data;

                              return (
                                <a
                                  href={`/users/profile/${userId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontWeight: "bold" }}
                                >
                                  {item?.data}
                                </a>
                              );
                            })()
                          ) : (
                            <span style={{ fontWeight: "bold", color: "#000" }}>
                              {item?.data}
                            </span>
                          )}
                        </p>
                      ))}
                  </Col>
                  <Col xs={12} md={3}>
                    {profileDetails
                      .slice(5, 10)
                      .map((item: any, index: number) => (
                        <p
                          key={index}
                          style={{ color: "#858D9D", fontSize: 14 }}
                          className="my-2"
                        >
                          {item?.name}:{" "}
                          <span style={{ fontWeight: "bold", color: "#000" }}>
                            {item?.data}
                          </span>
                        </p>
                      ))}
                  </Col>
                  <Col xs={12} md={6}>
                    <Card className="shadow-sm border-1 mb-0">
                      {/* HEADER */}
                      <CardHeader className="d-flex justify-content-between align-items-center bg-white pt-3 px-3">
                        <div>
                          <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                            <i className="bx bx-chip"></i>
                            {deviceDetails.slave}
                            <span className="text-muted">
                              ({deviceDetails.deviceType})
                            </span>
                          </h5>
                        </div>

                        <Badge
                          color={deviceDetails.isOnline ? "success" : "danger"}
                        >
                          {deviceDetails.isOnline ? "Online" : "Offline"}
                        </Badge>
                      </CardHeader>

                      {/* BODY */}
                      <CardBody className="py-2 px-3">
                        <div className="row g-2" style={{ fontSize: "13px" }}>
                          {/* LEFT COLUMN */}
                          <div className="col-6 row g-1">
                            <div>
                              <strong>Product:</strong>{" "}
                              {deviceDetails.product || "--"}
                            </div>
                            <div>
                              <strong>Model:</strong>{" "}
                              {deviceDetails.modelType || "--"}
                            </div>

                            <div>
                              <strong>HW:</strong>{" "}
                              {deviceDetails.hwVersion || "--"}
                            </div>
                            <div>
                              <strong>WiFi:</strong>{" "}
                              {deviceDetails.wifiVersion || "--"}
                            </div>
                            <div>
                              <strong>Mcu Brand:</strong>{" "}
                              {deviceDetails.mcu_brand || "-"}
                            </div>
                          </div>

                          {/* RIGHT COLUMN */}
                          <div className="col-6 row g-1">
                            <div>
                              <strong>SSID:</strong>{" "}
                              {deviceDetails.ssid || "--"}
                            </div>
                            <div>
                              <strong>Signal:</strong>{" "}
                              <span
                                style={{
                                  color:
                                    deviceDetails.signalStrength > 80
                                      ? "green"
                                      : deviceDetails.signalStrength > 50
                                      ? "orange"
                                      : "red",
                                }}
                              >
                                {deviceDetails.signalStrength}%
                              </span>
                            </div>

                            <div>
                              <strong>THW:</strong> {deviceDetails.thw || "--"}
                            </div>
                            <div>
                              <strong>TSW:</strong> {deviceDetails.tsw || "--"}
                            </div>
                            <div>
                              <strong>RoomName:</strong>{" "}
                              {deviceDetails.roomName || "--"}
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </>
              ) : (
                <Col
                  xs={12}
                  md={12}
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "155px" }}
                >
                  <Spinner color="dark" />
                </Col>
              )}
            </Row>
          </CardBody>
        </Collapse>
      </Card>

      <Modal
        isOpen={isSSModalOpen}
        toggle={() => setIsSSModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setIsSSModalOpen(false)}>
          <div className="d-flex gap-4 ">
            <div className=" pe-4 ">SS Command Data</div>
            <div className="d-flex justify-content-center align-items-center gap-1 ps-4">
              <input
                type="checkbox"
                checked={filterOnline}
                onChange={(e) => setFilterOnline(e.target.checked)}
              />
              <div className="text-muted small fs-6 fw-bold">Online</div>
            </div>
            <div className="d-flex justify-content-center align-items-center gap-1">
              <input
                type="checkbox"
                checked={filterOffline}
                onChange={(e) => setFilterOffline(e.target.checked)}
              />
              <div className="text-muted small fs-6 fw-bold">Offline</div>
            </div>
            <div className="d-flex justify-content-center align-items-center gap-1">
              <input
                type="checkbox"
                checked={filterPowerOn}
                onChange={(e) => setFilterPowerOn(e.target.checked)}
              />
              <div className="text-muted small fs-6 fw-bold">Power ON</div>
            </div>
            <div className="d-flex justify-content-center align-items-center gap-1">
              <input
                type="checkbox"
                checked={filterPowerOff}
                onChange={(e) => setFilterPowerOff(e.target.checked)}
              />
              <div className="text-muted small fs-6 fw-bold">Power OFF</div>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          {ssData?.data ? (
            <>
              {/* Search Node ID */}
              <div className="row g-2 mb-3">
                <div className="col">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="text-muted small fs-6 fw-bold">
                      Total Nodes
                    </div>
                    <div className="fw-bold fs-4">{ssData?.data?.total}</div>
                  </div>
                </div>

                <div className="col">
                  <div className="border rounded p-2 text-center">
                    <div className="text-muted small fs-6 fw-bold">Online</div>
                    <div className="fw-bold fs-4 text-success">
                      {
                        ssData.data.status.filter((status: any) =>
                          String(status).endsWith("1"),
                        ).length
                      }
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="border rounded p-2 text-center">
                    <div className="text-muted small fs-6 fw-bold">Offline</div>

                    <div className="fw-bold fs-4 text-danger">
                      {
                        ssData.data.status.filter((status: any) =>
                          String(status).endsWith("0"),
                        ).length
                      }
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="border rounded p-2 text-center">
                    <div className="text-muted small fs-6 fw-bold">
                      Power ON
                    </div>
                    <div className="fw-bold fs-4 text-success">
                      {
                        ssData.data.status.filter((status: any) =>
                          String(status).startsWith("1"),
                        ).length
                      }
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="border rounded p-2 text-center">
                    <div className="text-muted small fs-6 fw-bold">
                      Power OFF
                    </div>
                    <div className="fw-bold fs-4 text-danger">
                      {
                        ssData.data.status.filter((status: any) =>
                          String(status).startsWith("0"),
                        ).length
                      }
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bx bx-search"></i>
                  </span>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Node ID..."
                    value={nodeSearch}
                    onChange={(e) => setNodeSearch(e.target.value)}
                  />

                  {nodeSearch && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setNodeSearch("")}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  )}
                </div>
              </div>

              <div
                className="table-responsive"
                style={{ maxHeight: "60vh", overflowY: "auto" }}
              >
                <table className="table table-bordered table-sm mb-2">
                  <thead>
                    <tr>
                      <th>Node ID</th>
                      <th>Brightness</th>
                      <th>Temp</th>
                      <th>Status</th>
                      <th>Power</th>
                      <th>Connection</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows?.map(({ nodeId, idx }: any) => {
                      const status = String(ssData.data.status?.[idx] ?? "");
                      const isPowerOn = status.startsWith("1");
                      const isOnline = status.endsWith("1");

                      return (
                        <tr key={idx}>
                          <td className="fw-semibold">{nodeId}</td>
                          <td>{ssData.data.brightness?.[idx] ?? "--"}</td>
                          <td>{ssData.data.temp?.[idx] ?? "--"}</td>
                          <td>{status || "--"}</td>
                          <td>
                            <span
                              className={`fw-semibold ${
                                isPowerOn ? "text-success" : "text-danger"
                              }`}
                            >
                              {isPowerOn ? "ON" : "OFF"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`fw-semibold ${
                                isOnline ? "text-success" : "text-danger"
                              }`}
                            >
                              {isOnline ? "Online" : "Offline"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}

                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-3">
                          No Node ID found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div>No data available</div>
          )}

          <div style={{ fontSize: 13, color: "#858D9D" }}>
            Device ID: {ssData?.device_ID || "--"} &nbsp;|&nbsp; Serial:{" "}
            {ssData?.serial ?? "--"} &nbsp;|&nbsp; Error:{" "}
            {ssData?.error ?? "--"} &nbsp;|&nbsp; Total Node:{" "}
            {filteredRows.length}
          </div>
        </ModalBody>
      </Modal>

      {/* FILTER BAR */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Card className="mx-4 mb-0 mt-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-end">
              <Col xs={6} md={2}>
                <label className="form-label small fw-semibold mb-1">
                  Start Date
                </label>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={filters.startDate}
                  onChange={(v) => handleDateTimeChange("startDate", v)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </Col>
              <Col xs={6} md={2}>
                <label className="form-label small fw-semibold mb-1">
                  End Date
                </label>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={filters.endDate}
                  onChange={(v) => handleDateTimeChange("endDate", v)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </Col>
              <Col xs={6} md={2}>
                <label className="form-label small fw-semibold mb-1">
                  Start Time
                </label>
                <MobileTimePicker
                  ampm
                  format="hh:mm A"
                  value={filters.startTime}
                  onChange={(v) => handleDateTimeChange("startTime", v)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </Col>
              <Col xs={6} md={2}>
                <label className="form-label small fw-semibold mb-1">
                  End Time
                </label>
                <MobileTimePicker
                  ampm
                  format="hh:mm A"
                  value={filters.endTime}
                  onChange={(v) => handleDateTimeChange("endTime", v)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label small fw-semibold mb-1">
                  Command
                </label>
                <Dropdown isOpen={isCmdOpen} toggle={toggleCmdDropdown}>
                  <DropdownToggle
                    tag="button"
                    type="button"
                    className="form-select text-start text-truncate w-100 bg-white py-2 "
                  >
                    {filters.cmd.length ? filters.cmd.join(", ") : "All"}
                  </DropdownToggle>

                  <DropdownMenu className="w-100 p-0">
                    <div className="border-bottom px-3 py-2">
                      {/* search bar for cmd */}
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white">
                          <i className="bx bx-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search command..."
                          value={cmdSearch}
                          onChange={(e) => setCmdSearch(e.target.value)}
                          autoFocus
                        />
                        {cmdSearch && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setCmdSearch("")}
                          >
                            <i className="bx bx-x"></i>
                          </button>
                        )}
                      </div>

                      <div className="d-flex justify-content-between pt-2">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={selectAllCmds}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-danger"
                          onClick={clearCmds}
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div
                      className="overflow-auto px-3 py-2"
                      style={{ maxHeight: 240 }}
                    >
                      {filteredCmdOptions.length === 0 && (
                        <div className="text-muted small">
                          No commands found
                        </div>
                      )}
                      {filteredCmdOptions.map((c) => (
                        <div className="form-check mb-1" key={c}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`cmd-${c}`}
                            checked={filters.cmd.includes(c)}
                            onChange={() => toggleCmd(c)}
                          />
                          <label
                            className="form-check-label w-100"
                            htmlFor={`cmd-${c}`}
                          >
                            {c}
                          </label>
                        </div>
                      ))}
                    </div>
                  </DropdownMenu>
                </Dropdown>
              </Col>

              <Col xs={12} md={2} className="d-flex gap-2">
                <button
                  className="btn btn-dark flex-fill"
                  onClick={handleApplyFilters}
                >
                  Search
                </button>
                <button
                  className="btn btn-outline-secondary flex-fill"
                  onClick={handleResetFilters}
                >
                  Reset
                </button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </LocalizationProvider>

      <ReactDataTable
        key={JSON.stringify(appliedFilters)}
        apiURL={`${url.BASE_URL}${url.DEBUG_DETAIL_LIST}`}
        paramsId={{ slave: slave, ...appliedFilters }}
        columns={columns}
        isReload={isReload}
        setTxCount={setTxCount}
        setRxCount={setRxCount}
        setCmdCounts={setCmdCounts}
        cmdList={
          <div
            className="d-flex flex-wrap"
            style={{ gap: "12px", fontSize: "16px" }}
          >
            {Object.entries(cmdCounts as { [key: string]: number })
              ?.sort(([a], [b]) => {
                if (a === "ERROR") return 1;
                if (b === "ERROR") return -1;
                if (a === "NO CMD") return 1;
                if (b === "NO CMD") return -1;
                return a.localeCompare(b);
              })
              ?.map(([cmd, count], i) => (
                <div
                  key={`${cmd}-${i}`}
                  className={`d-flex justify-content-center align-items-center border gap-2 px-2 py-2 ${
                    cmd === "ERROR"
                      ? "bg-danger text-white rounded"
                      : "bg-light rounded"
                  }`}
                  style={{
                    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                  }}
                >
                  <small className="text-uppercase fw-semibold">
                    {cmd === "ERROR" ? "ERRORS" : cmd}
                  </small>
                  <span className="badge bg-primary rounded-pill ">
                    {count}
                  </span>
                </div>
              ))}
          </div>
        }
        buttonformatJSON={
          <FormatJsonCheckBox
            checked={showFormatted}
            onChange={() => setShowFormatted(!showFormatted)}
          />
        }
      />
    </>
  );
};

export default DebugDetail;